import { ReactComponent as IconClose } from '@/assets/svgs/icon-close.svg';
import Breadcrumbs from '@/components/Breadcrumbs';
import Button from '@/components/Button/index';
import Input from '@/components/Input/index';
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import Modal from "@/componentsDS/Modal/Modal";
import Portal from '@/components/Portal';
import SelectorBox3 from '@/components/SelectorBox3';
import StepList from '@/components/StepList/index';
import Toast from '@/components/Toast';
import Stepper from '@/componentsDS/Stepper/Stepper';
import useModal from '@/hooks/use-modal/index';
import usePage from '@/hooks/use-page';
import usePitstop from '@/hooks/use-pitstop';
import useStepList from '@/hooks/use-step-list';
import useToast from '@/hooks/use-toast';
import { useNavigate, useLocation } from '@/lib/router-dom';
import api from '@/services/api/pitstopConfigure';
import rem from '@/utils/rem.js';
import validatePitstopShortName from "@/utils/validate-pitstop-short-name.js";
import validateURL from "@/utils/validate-url.js";
import produce from 'immer';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import areUrlsEqual from '@/utils/are-urls-equal';
import { useTradex } from '@/hooks/use-tradex/index';

const Page = () => {

	/*=================================================1
		History
	/================================================*/
	const { goTo } = useNavigate();
	const { currentState } = useLocation();

	let preloadedValues = {}

	if (currentState) {
		preloadedValues = {
			pitstopname: currentState.data.systemName,
			pitstopdescription: currentState.data.desc,
			endpointURL: currentState.data.endPointUrl,
			orgId: currentState.data.orgId,
			systemId: currentState.data.systemId,
			mode: currentState.mode
		}
	}

	/*=================================================1
		General
	/================================================*/

	const toastType = {
		success: "success",
		error: "error"
	}

	const [pageTitle, setPageTitle] = useState("Set Up New Pitstop");
	const environments = useSelector(state => state.auth.environments)


	const { breadcrumbsItems } = usePage({ title: pageTitle, breadcrumbs: [pageTitle, 'Setup New Pitstop'] })

	const cardStyles = {
		backgroundImage: `url("../../pitstop-banner.png")`
	}

	const user = useSelector(state => state.auth.user)

	const [loading, setLoading] = useState(false)

	/*=================================================
		API
	/================================================*/

	const { toastList, addToastMessage } = useToast()
	const { items: pitstopItems, fetch: fetchPitstop } = usePitstop()

	/*=================================================
		Steps
	/================================================*/
	// const [steps] = useState(['Select Hosting Method', 'Select Authentication Method'])
	const steps = [{
		stepTitle: 'Select Hosting Method',
	}, {
		stepTitle: 'Select Authentication Method',
	}]
	const { step, goToStep, prevStep, nextStep } = useStepList(steps)

	const [selectedConfig, setSelectedConfig] = useState("")
	const [inputUrl, setInputUrl] = useState("")

	const [payload, setPayload] = useState("");
	const [endpointURL, setEndpointURL] = useState(environments?.pitstopHost);
	const [inlineErrors, setInlineErrors] = useState(false)

	const validatePitstopHostUrl = (event) => {
		event.target.value = event.target.value?.trim();
		const endpointURL = event.target.value?.toLowerCase();
		const existingEndpointURL = preloadedValues?.endpointURL?.toLowerCase()?.trim();
		if (preloadedValues?.mode === "edit" && existingEndpointURL === endpointURL) {
			return;
		}
		const existing = pitstopItems?.find(pitstop => pitstop.endPointUrl?.toLowerCase()?.trim() === endpointURL);
		setInlineErrors(existing ? { ...inlineErrors, endpointURL: "Hostname already exists. Please enter another hostname." } : { ...inlineErrors, endpointURL: "" })
	}

	const validatePitstopShortNameForEndpointUrl = (urlToValidate = endpointURL) => {
		const existingEndpointURL = preloadedValues?.endpointURL?.toLowerCase()?.trim();
		if (preloadedValues?.mode === "edit" && existingEndpointURL === urlToValidate) {
			return;
		}
		const existing = pitstopItems?.find(pitstop => areUrlsEqual(pitstop.endPointUrl, urlToValidate));
		setInlineErrors(
			existing
				? { ...inlineErrors, pitstopshortname: "This Pitstop Short Name already exists. Please enter another short name." }
				: { ...inlineErrors, pitstopshortname: "" }
		);
	};


	const validatePitstopName = (event) => {
		const pitstopName = event.target.value?.toLowerCase()?.trim();
		const existingPitstopName = preloadedValues?.pitstopname?.toLowerCase()?.trim();
		if (preloadedValues?.mode === "edit" && existingPitstopName === pitstopName) {
			return;
		}
		const existing = pitstopItems?.find(pitstop => pitstop.name?.toLowerCase()?.trim() === pitstopName);
		setInlineErrors(existing ? { ...inlineErrors, pitstopname: "This Pitstop Name already exists. Please enter another name." } : { ...inlineErrors, pitstopname: "" })
	}


	useEffect(() => {
		if (preloadedValues?.mode === "edit") {
			setPageTitle("Edit Pitstop")
			goToStep(2)
		}
	}, [preloadedValues])

	/*=================================================
		Error Modal
	/================================================*/
	const { visible: isVisible, show: showPitstopConfigCancelModal, hide: hidePitstopConfigCancelModal } = useModal()
	const { visible: isSubmitModalVisible, show: showPitstopSubmitModal, hide: hidePitstopSubmitModal } = useModal()
	const { visible: errorVisible, show: showErrorPitstopModal, hide: hideErrorPitstopModal } = useModal()

	/*=================================================
		Configuration List
	/================================================*/
	const isTradex = useTradex();

	const [configLists, setConfigLists] = useState([
		{
			id: 1,
			value: "CDI",
			name: isTradex ? "SGTradex Hosted" : "Hosted",
			$selected: false,
			$disabled: false
		}
	])

	const handleUpdateConfigSelect = (item) => {
		const index = configLists.findIndex(config => config.id === item.id);
		const updatedItems = produce(configLists, draft => {
			draft.forEach(
				config => config.$selected = false
			)
			draft[index].$selected = true
		})
		setSelectedConfig(item.value);
		setConfigLists(updatedItems);
	}

	/*=================================================
		History
	/================================================*/

	useEffect(() => {
		fetchPitstop();

		if (currentState?.mode) {

			const index = configLists.findIndex(config => config.value === currentState.data?.host);
			const updatedItems = produce(configLists, draft => {
				draft.forEach(
					config => config.$selected = false
				)
				draft[index].$selected = true
			})

			setSelectedConfig(currentState.data?.host)
			setConfigLists(updatedItems);
		}
	}, [])


	const { register, handleSubmit, errors, setValue, formState: { isSubmitting, isDirty, isValid } } = useForm({ mode: 'onChange', defaultValues: preloadedValues })
	const [formFieldState, setFormFieldState] = useState({
		systemName: "",
		endPointUrl: ""
	})


	async function formHandler(data) {


		let apiData = {};
		const isEmpty = Object.values(data).some(x => x === '' || x.trim() === '');

		if (isEmpty) {
			return showErrorPitstopModal();
		}

		if (selectedConfig === 'CDI') {
			apiData = {
				systemName: data.pitstopname,
				desc: data.pitstopdescription,
				host: selectedConfig,
				endPointUrl: endpointURL,
				orgId: user.organizationId
			}
		}
		if (selectedConfig === 'SELF') {
			apiData = {
				systemName: data.pitstopname,
				desc: data.pitstopdescription,
				host: selectedConfig,
				endPointUrl: data.endpointURL,
				orgId: user.organizationId
			}
		}

		setPayload(apiData);
		showPitstopSubmitModal();
	}

	async function updatePitstop(data) {
		setLoading(true)
		if (preloadedValues?.mode !== "edit") {

			const [error, result] = await api.create(data);

			setLoading(false)
			if (error) {
				return addToastMessage("Failed to Save Pitstop", toastType.error)
				// return setError(error?.error?.message || "Failed to Save Pitstop")
			}
			return goTo(`/pitstop/configuration/success`, { data: result, mode: "create" })
		}

		const [error, result] = await api.updatePitstop(preloadedValues?.systemId, data);
		setLoading(false)
		if (error) {
			return addToastMessage("Failed to Save Pitstop", toastType.error)
			// return setError(error?.error?.message || "Failed to Save Pitstop")
		}
		goTo(`/pitstop/configuration/success`, { data: result, mode: "edit" })

	}

	const ConfigurationList = () => {

		const list = configLists.map((config, index) => {

			return (
				<div className="checkbox-selector-box__col checkbox-selector-box3 checkbox-selector-box__col_remove-flex-basis" key={index}>
					<SelectorBox3 label={config.name}
						submit={() => {

							handleUpdateConfigSelect(config)

						}
						}
						active={config.$selected}
						// disabled
						disabled={config.$disabled}
					/>
				</div>
			)
		})

		return (
			<div className='checkbox-selector-box__cols'>
				{list}
			</div>
		)
	}

	function checkProperties(obj) {
		for (let key in obj) {
			if (obj[key] === "") return false;
		}
		return true;
	}

	const isFormInvalid = !checkProperties(formFieldState) || !isValid || loading || inlineErrors.pitstopname || inlineErrors.pitstopshortname || inlineErrors.endpointURL;

	return (
		<>
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>
				<div className="title-header__right">
					<div className="overview__filter overview__button_wrap">
					</div>
				</div>
			</div>

			{preloadedValues?.mode !== "edit" && (
				<Stepper steps={steps} currentStep={step - 1} />
			)}


			<hr />

			<div>
				<form data-testid="form" id="authentication" onSubmit={handleSubmit(data => formHandler(data))}>
					{(step === 1) && (
						<>
							<ConfigurationList />
						</>
					)}

					{
						(step === 2) && (
							<>

								{
									(selectedConfig === 'CDI') && (
										<>
											<div className="pitstop-sourcesystem__col">

												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label" style={{ marginBottom: "1%" }}>
													Pitstop Name
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input">
														<Input
															name="pitstopname"
															label=""
															onFocus={validatePitstopName} onBlur={validatePitstopName}
															register={register({
															})}
															onChange={(e) => {
																setFormFieldState({ ...formFieldState, endPointUrl: e.target.value.trim() })
																validatePitstopName(e)
															}}
															placeholder=""
															error={errors.pitstopname || inlineErrors.pitstopname}
															errorMessage={errors.pitstopname?.message || inlineErrors.pitstopname}
														/>


													</div>
												</div>
											</div>

											<div className="pitstop-sourcesystem__col">

												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label" style={{ marginBottom: "2%" }}>
													Pitstop Short Name
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input">

														<Input
															name="pitstopshortname"
															label=""
															register={register({
																validate: {
																	checkValidPistopShortName: v => validatePitstopShortName(v) || "Please Provide valid Pitstop Short Name"
																}
															})}
															onChange={(e) => {
																let pitstopHost = environments?.pitstopHost;
																pitstopHost = pitstopHost.replace("{{SystemShortName}}", e.target.value.toLowerCase());
																setEndpointURL(pitstopHost);
																setFormFieldState({ ...formFieldState, systemName: e.target.value.trim() })
																validatePitstopShortNameForEndpointUrl(pitstopHost)
															}}
															placeholder=""
															error={errors.pitstopshortname || inlineErrors.pitstopshortname}
															errorMessage={errors.pitstopshortname?.message || inlineErrors.pitstopshortname}
															onBlur={(e) => {
																e.target.value = e.target.value.trim();
																validatePitstopShortNameForEndpointUrl();
															}}
														/>

														<label htmlFor='pitstopshortname' className='pitstop-sourcesystem__col__right_input--label'>Note: Please provide short name without spaces.</label>
													</div>
												</div>
											</div>

											<div className="pitstop-sourcesystem__col">

												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label" style={{ marginBottom: "2%" }}>
													Pitstop URL
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input">
														<input name="endpointURL" type="text" disabled value={endpointURL} />
														<label htmlFor='endpointURL' className='pitstop-sourcesystem__col__right_input--label'>Note: This will be your production Pitstop URL</label>
													</div>
												</div>
											</div>


											<div className="pitstop-sourcesystem__col pitstop-sourcesystem__col--top-align">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem__col__left--margin-top pitstop-sourcesystem_col_left_label">
													Description
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right--flex pitstop-sourcesystem__col__right--flex_end pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input">
														<textarea placeholder='Enter a short description about your system. This will be displayed to other participants’ when selecting a system.'
															ref={register(selectedConfig === 'CDI')} name="pitstopdescription"
														/>
														<textarea name="pitstopdescription" style={{ display: "none" }} />
													</div>

												</div>
											</div>

										</>
									)
								}

								{
									selectedConfig === 'SELF' && (
										<>
											<div className="pitstop-sourcesystem__col">

												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label" style={{ marginBottom: "1%" }}>
													Pitstop Name
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input">

														<Input
															name="pitstopname"
															label=""
															onFocus={validatePitstopName} onBlur={validatePitstopName}
															register={register({
															})}
															onChange={(e) => {
																setFormFieldState({ ...formFieldState, systemName: e.target.value.trim() })
															}}
															placeholder=""
															error={errors.pitstopname || inlineErrors.pitstopname}
															errorMessage={errors.pitstopname?.message || inlineErrors.pitstopname}
														/>
													</div>
												</div>
											</div>



											<div className="pitstop-sourcesystem__col pitstop-sourcesystem__col--top-align">
												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem__col__left--margin-top pitstop-sourcesystem_col_left_label">
													Pitstop Description
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right--flex pitstop-sourcesystem__col__right--flex_end pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input">
														<textarea placeholder='Enter a short description about your system. This will be displayed to other participants’ when selecting a system.'
															ref={register(selectedConfig === 'SELF')} name="pitstopdescription"
														/>
														<textarea name="pitstopdescription" style={{ display: "none" }} />
													</div>

												</div>
											</div>

											<div className="pitstop-sourcesystem__col">

												<div className="pitstop-sourcesystem__col__left pitstop-sourcesystem_col_left_label" style={{ marginBottom: "3%" }}>
													Pitstop Hostname
												</div>
												<div className="pitstop-sourcesystem__col__right pitstop-sourcesystem__col__right_input_wrap">
													<div className="pitstop-sourcesystem__col__right_input">

														<Input
															name="endpointURL"
															label=""
															onFocus={validatePitstopHostUrl} onBlur={validatePitstopHostUrl}
															register={register({
																validate: {
																	checkValidURL: v => validateURL(v) || "Please Provide Valid URL"
																}
															})}
															onChange={(e) => {
																setFormFieldState({ ...formFieldState, endPointUrl: e.target.value.trim() });

															}}
															placeholder=""
															error={errors.endpointURL || inlineErrors.endpointURL}
															errorMessage={errors.endpointURL?.message || inlineErrors.endpointURL}
														/>

														<label htmlFor='endpointURL' className='pitstop-sourcesystem__col__right_input--label'>Note: You will need a hostname to exchange data on a self-hosted pitstop.</label>
													</div>
												</div>
											</div>
										</>
									)
								}

							</>
						)
					}


				</form>
			</div>
			<div className='DS__layout__footer'>
				<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						<Button text='Cancel' full noborder maxWidth='154'
							submit={() => {
								showPitstopConfigCancelModal()
								goTo('/pitstop')
							}}
						/>
					</div>
					<div className="layout-default__sticky-actions__right layout-default--flex">
						{step === 1 &&
							<div className="layout-default__sticky-actions__right-flex">
								<Button text='Next' full maxWidth='154'
									submit={() => {
										nextStep()
									}}
									disabled={!selectedConfig}
								/>
							</div>
						}

						{step === 2 &&
							<div className="layout-default__sticky-actions__right-flex ">
								{preloadedValues?.mode !== "edit" && <div className="layout-default--margin-right">
									<Button text="Previous" ghost submit={() => {
										prevStep()
										setFormFieldState({ ...formFieldState, endPointUrl: "", systemName: "" })
									}} />
								</div>
								}
								<Button form="authentication" text={"Submit"} full maxWidth="154" disabled={isFormInvalid} loading={loading} />
							</div>

						}
					</div>
				</div>
			</div>

			<Modal visible={isVisible} variant='medium' close={hidePitstopConfigCancelModal}>
				<CardConfigure>
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Cancel Pitstop Configuration
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: rem(16), textAlign: 'center' }}>Are you sure you want to cancel?</div>
							<br />
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel"
								submit={() => {
									hidePitstopConfigCancelModal()
								}} />

							<span style={{ width: rem(10), display: 'inline-block' }}></span>

							<Button text="Confirm"
								submit={() => {
									goTo('/pitstop')
								}} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={isSubmitModalVisible} variant='medium' close={hidePitstopSubmitModal}>
				<CardConfigure>
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Save Pitstop Configuration
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: rem(16), textAlign: 'center' }}>Are you sure you want save?</div>
							<br />
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel"
								submit={() => {
									hidePitstopSubmitModal()
								}} />

							<span style={{ width: rem(10), display: 'inline-block' }}></span>

							<Button text="Confirm"
								id="create-new-pitstop-confirm-button"
								submit={() => {
									hidePitstopSubmitModal();
									updatePitstop(payload);
								}} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Modal visible={errorVisible} variant='medium' close={hideErrorPitstopModal}>
				<CardConfigure>
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Error Pitstop Configuration
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: rem(16), textAlign: 'center' }}>Please enter all the details properly</div>
							<br />
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel"
								submit={() => {
									hideErrorPitstopModal()
								}} />
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Toast toastList={toastList} />
		</>
	)

}

export default Page;
